import {
  ShieldCheck,
  CircleDollarSign,
  Clock,
  FileText,
  Dog,
  Stethoscope,
  MessageCircle,
  Camera,
  Star,
  Search,
  Smartphone,
} from 'lucide-react'
import { siteConfig } from './siteConfig'
import type {
  Stat,
  BeforeAfterRow,
  IconItem,
  Tool,
  Bonus,
  MessageCategory,
  ChatMessage,
  Step,
  OfferItem,
  Faq,
  Testimonial,
} from '../types/landing'

export const images = {
  heroKit:
    'https://cdn.magicpatterns.com/uploads/ceEDQs28aF1zdzz8Shdfcs/Paseo_Pro__kit_para_paseadores.png',
  postsGallery:
    'https://cdn.magicpatterns.com/patterns/generated-images/417d9bb3-bc0c-4e64-8920-c733b89f8961.jpg',
}

export const pains: string[] = [
  'Te cancelan media hora antes y no sabes cómo cobrarlo sin quedar mal.',
  'No sabes cuánto cobrar, así que cobras “lo que cobran todos”… y te quedas corto/a.',
  'Te da vergüenza reclamar un pago atrasado, y terminas trabajando gratis.',
  'El perro se lastima y no tienes el teléfono de su veterinario ni permiso para actuar.',
  'Respondes cada consulta desde cero y muchas nunca se convierten en clientes.',
  'Sabes que deberías publicar en Instagram, pero no sabes qué… y no publicas nada.',
]

export const stats: Stat[] = [
  { value: '6', label: 'herramientas listas' },
  { value: '50', label: 'mensajes de WhatsApp' },
  { value: '30', label: 'posts diseñados' },
  { value: '3', label: 'bonos de regalo' },
]

export const introChecks: string[] = [
  'Documentos que te protegen a ti y a tus clientes',
  'Precios calculados con números, no “a ojo”',
  'Mensajes que venden, cobran y ponen límites sin incomodar',
  'Un mes completo de contenido para tus redes',
]

export const beforeAfter: BeforeAfterRow[] = [
  { before: 'Acuerdos de palabra que nadie recuerda', after: 'Reglas claras por escrito y firmadas' },
  { before: 'Precios inventados “a ojo”', after: 'Precios calculados con tus costos reales' },
  { before: 'Cobrar te da vergüenza', after: 'Mensajes de cobro amables y firmes' },
  { before: 'Una emergencia es puro pánico', after: 'Autorización veterinaria firmada' },
  { before: 'Instagram vacío o abandonado', after: '30 días de contenido listo' },
  { before: 'Consultas que se enfrían', after: 'Respuestas que convierten en clientes' },
]

export const benefits: IconItem[] = [
  {
    icon: ShieldCheck,
    title: 'Te ves profesional',
    text: 'Los clientes que pagan bien eligen a quien les da confianza. Un contrato y una ficha cambian cómo te perciben desde el primer día.',
  },
  {
    icon: CircleDollarSign,
    title: 'Cobras mejor',
    text: 'Sabes cuánto cobrar, cobras las cancelaciones tardías y reclamas pagos sin incomodidad.',
  },
  {
    icon: Clock,
    title: 'Ahorras horas',
    text: 'No más pensar qué responder ni qué publicar. Copias, personalizas y listo.',
  },
]

export const tools: Tool[] = [
  {
    icon: FileText,
    label: 'Herramienta 1',
    title: 'Contrato de servicio',
    description:
      '10 cláusulas claras y cortas para que nadie te tome el pelo: precio y pagos, cancelaciones, llaves, salud, emergencias, responsabilidad, fotos en redes y cómo terminar el servicio.',
    formats: ['PDF para imprimir', 'Word editable', '2 páginas'],
  },
  {
    icon: Dog,
    label: 'Herramienta 2',
    title: 'Ficha de la mascota',
    description:
      'Todo sobre cada perro en un solo lugar: datos, salud, medicación, veterinario de cabecera, contactos de emergencia, cómo se lleva con otros perros y niños, miedos, rutina y reglas de la casa.',
    formats: ['PDF para imprimir', 'Word editable', '2 páginas'],
  },
  {
    icon: Stethoscope,
    label: 'Herramienta 3',
    title: 'Autorización veterinaria de emergencia',
    description:
      'Para actuar rápido cuando más importa: veterinaria preferida, límite de gasto, qué hacer si el tutor no responde y compromiso de reembolso. Tranquilidad para ti y para la familia.',
    formats: ['PDF para imprimir', 'Word editable', '1 página'],
  },
  {
    icon: CircleDollarSign,
    label: 'Herramienta 4',
    title: 'Lista de precios + calculadora',
    description:
      'Un menú de servicios lindo para enviar a tus clientes (paseos, packs, guardería, visitas, extras) y una guía simple con fórmula para calcular cuánto cobrar según tus costos y lo que quieres ganar.',
    formats: ['PDF', 'Word editable', 'Fórmula paso a paso'],
  },
  {
    icon: MessageCircle,
    label: 'Herramienta 5',
    title: '50 mensajes de WhatsApp listos para copiar',
    description:
      'Para cada momento: responder la primera consulta, cerrar la venta, dar la bienvenida, reportar el paseo, cobrar, recordar un pago, manejar cancelaciones, comunicar un incidente y pedir reseñas.',
    formats: ['PDF visual', 'Word para copiar', '7 situaciones'],
  },
  {
    icon: Camera,
    label: 'Herramienta 6',
    title: '30 posts diseñados para tus redes',
    description:
      'Un mes completo de contenido para Instagram y Facebook: tips que se guardan y comparten, posts de venta, confianza y preguntas que generan comentarios. Cada imagen viene con su texto y hashtags.',
    formats: ['30 imágenes 1080×1350', '30 textos + hashtags', 'Sin Canva'],
  },
]

export const bonuses: Bonus[] = [
  {
    tag: 'Bono 1',
    title: 'Tus primeros 10 clientes en 30 días',
    text: 'Un plan día a día para conseguir clientes: dónde encontrarlos, qué publicar y cómo cerrar la venta.',
    image: 'https://cdn.magicpatterns.com/uploads/wcuEy9tdcXSmCK2tcwHcAR/paseo_1.webp',
  },
  {
    tag: 'Bono 2',
    title: 'Planilla de clientes, paseos y pagos',
    text: 'Cada cliente, paseo y pago en un solo lugar. Sabes quién te debe y cuánto ganaste en el mes.',
    image: 'https://cdn.magicpatterns.com/uploads/8ss5XKFffc6HATxKapif1L/paseo_2.webp',
  },
  {
    tag: 'Bono 3',
    title: 'Checklist de seguridad del paseador',
    text: 'Qué revisar antes, durante y después de cada paseo para evitar escapes, peleas y accidentes.',
    image: 'https://cdn.magicpatterns.com/uploads/9oYkiFDbjxy3rcHiwFYRqc/paseo_3.webp',
  },
]

export const messageCategories: MessageCategory[] = [
  { label: 'Primer contacto y ventas', count: 10 },
  { label: 'Bienvenida', count: 6 },
  { label: 'Durante el servicio', count: 10 },
  { label: 'Pagos y cobranzas', count: 7 },
  { label: 'Cancelaciones y clima', count: 6 },
  { label: 'Situaciones delicadas', count: 5 },
  { label: 'Fidelización y reseñas', count: 6 },
]

export const chatMessages: ChatMessage[] = [
  { from: 'client', text: 'Hola! Vi tu publicación, ¿paseas perros grandes? 🐕' },
  {
    from: 'me',
    text: '¡Hola [Nombre]! 👋 Gracias por escribirme. Para recomendarte el mejor servicio, cuéntame: ¿cómo se llama tu perro, qué edad tiene y qué días necesitas? 🐾',
  },
  { from: 'client', text: 'Se llama Toto, tiene 3 años. Lunes a viernes de mañana' },
  {
    from: 'me',
    text: '¡Me encanta! Antes de empezar hago un encuentro de presentación sin cargo para conocer a [Perro]. ¿Te queda bien el [día] a las [hora]?',
  },
  { from: 'client', text: 'Perfecto, ¡nos vemos! 🙌' },
]

export const howItWorks: Step[] = [
  { title: 'Haces tu compra', text: 'Pago único y seguro. Sin suscripciones ni cobros sorpresa.' },
  {
    title: 'Lo recibes por email',
    text: 'Apenas se confirma el pago te llega el acceso al kit completo para descargar.',
  },
  {
    title: 'Pones tu nombre y listo',
    text: 'Editas los Word con tu marca, cargas los mensajes y publicas tu primer post. En una tarde está todo.',
  },
]

export const fitYes: string[] = [
  'Estás empezando como paseador/a o cuidador/a y quieres arrancar bien desde el día 1.',
  'Ya tienes clientes, pero trabajas con acuerdos de palabra y te cuesta poner límites.',
  'Quieres cobrar más y dejar de perder dinero con cancelaciones y pagos atrasados.',
  'Tienes una guardería, hospedaje o peluquería y quieres sumar paseos o cuidados.',
  'No tienes tiempo (ni ganas) de diseñar posts y escribir mensajes desde cero.',
]

export const fitNo: string[] = [
  'Buscas una app o un software de gestión con reservas automáticas.',
  'Buscas un curso de adiestramiento o comportamiento canino.',
  'No estás dispuesto/a a dedicar ni 30 minutos a poner tu nombre y tus precios.',
  'Ya tienes contratos, fichas, mensajes y contenido organizados y te funcionan.',
]

export const offerItems: OfferItem[] = [
  { icon: FileText, label: 'Contrato de servicio', value: 'USD 27' },
  { icon: Dog, label: 'Ficha de la mascota', value: 'USD 9' },
  { icon: Stethoscope, label: 'Autorización veterinaria', value: 'USD 9' },
  { icon: CircleDollarSign, label: 'Lista de precios + calculadora', value: 'USD 12' },
  { icon: MessageCircle, label: '50 mensajes de WhatsApp', value: 'USD 19' },
  { icon: Camera, label: '30 posts diseñados + textos', value: 'USD 37' },
  { icon: Star, label: '3 bonos de regalo', value: 'Incluidos' },
]

export const offerTotal = 'USD 113'

export const testimonials: Testimonial[] = [
  {
    quote:
      'Antes cobraba las cancelaciones de último momento con miedo. Ahora está en el contrato y nadie discute.',
    name: 'Fernanda Angoloti',
    location: 'Venezuela',
  },
  {
    quote:
      'Cargué los mensajes como respuestas rápidas y respondo consultas en segundos. Cerré dos clientes nuevos la primera semana.',
    name: 'Aníbal Gómez',
    location: 'Buenos Aires',
  },
  {
    quote:
      'La ficha del perro me salvó: un cliente se olvidó de avisarme una alergia y estaba anotada.',
    name: 'Pablo Bustos',
    location: 'Ciudad de México',
  },
  {
    quote:
      'Nunca sabía qué publicar. Con el calendario subo un post por día y ya me escriben por Instagram.',
    name: 'José López',
    location: 'Medellín',
  },
  {
    quote:
      'Usé la calculadora y me di cuenta de que cobraba muy poco. Subí mis precios y no perdí a nadie.',
    name: 'Micaela Fernández',
    location: 'Bogotá',
  },
]

export const refundSteps: Step[] = [
  {
    title: 'Nos escribes',
    text: `Dentro de los ${siteConfig.guaranteeDays} días posteriores a tu compra, envía un email a ${siteConfig.supportEmail} con el email que usaste para comprar.`,
  },
  {
    title: 'No te pedimos explicaciones',
    text: 'Si quieres contarnos qué no te gustó, nos ayuda a mejorar. Si no, no hace falta.',
  },
  {
    title: 'Recibes el reembolso',
    text: 'Procesamos la devolución del 100% por el mismo medio de pago que usaste.',
  },
]

export const supportHelps: IconItem[] = [
  {
    icon: Search,
    title: '¿No te llegó el email?',
    text: 'Revisa las carpetas de spam y promociones. Si no aparece, escríbenos con el email que usaste para comprar.',
  },
  {
    icon: FileText,
    title: '¿No tienes Word?',
    text: 'Abre los archivos gratis con Google Docs, desde la computadora o el celular.',
  },
  {
    icon: Smartphone,
    title: '¿Lo uso desde el celular?',
    text: 'Sí. Los PDF y los posts se ven y se comparten desde el celular sin problema.',
  },
]

export const faqs: Faq[] = [
  {
    question: '¿Cómo recibo el kit?',
    answer:
      'Apenas se confirma tu pago, te llega un email con el acceso para descargar todo el kit. Si no lo ves, revisa spam y promociones.',
  },
  {
    question: '¿En qué formato vienen los archivos?',
    answer:
      'Los documentos vienen en PDF (para imprimir) y en Word (para editar). Los posts vienen como imágenes PNG de 1080×1350, el tamaño ideal para Instagram, y sus textos en PDF y en Word.',
  },
  {
    question: '¿Puedo poner mi nombre y mi logo?',
    answer:
      'Sí. Los archivos Word están pensados para eso: cambias “Tu logo o nombre de tu servicio” por tu marca y guardas. También funcionan en Google Docs.',
  },
  {
    question: '¿Necesito saber diseño o usar Canva?',
    answer: 'No. Los 30 posts ya están diseñados y listos para subir. Solo copias el texto y publicas.',
  },
  {
    question: '¿Sirve en mi país?',
    answer:
      'Sí. Está escrito en español neutro y los precios van en blanco para que pongas tu moneda. Los documentos son modelos orientativos: te recomendamos adaptarlos y, si es posible, revisarlos con un abogado de tu país.',
  },
  {
    question: '¿Es un pago único o una suscripción?',
    answer: 'Pago único. Pagas una sola vez y el kit es tuyo para siempre. Sin cobros mensuales.',
  },
  {
    question: 'Recién estoy empezando y no tengo clientes. ¿Me sirve?',
    answer:
      'Es el mejor momento para usarlo. Los posts y los mensajes de venta te ayudan a conseguir tus primeros clientes, y los documentos hacen que te elijan a ti desde el primer contacto.',
  },
  {
    question: '¿Sirve si cuido gatos u otras mascotas?',
    answer:
      'Está pensado principalmente para perros. El contrato y la autorización veterinaria sirven para cualquier mascota; la ficha y los posts hablan de perros.',
  },
  {
    question: '¿Y si no me gusta?',
    answer: `Tienes ${siteConfig.guaranteeDays} días de garantía. Nos escribes y te devolvemos el 100% de tu dinero.`,
  },
  {
    question: '¿Puedo revender o compartir el kit?',
    answer:
      'No. La licencia es para uso personal en tu propio negocio. Puedes usar los documentos con todos tus clientes y publicar los posts en tus redes, pero no revender ni regalar los archivos.',
  },
]
